// Honeybadger JavaScript error reporting configuration
import Honeybadger from '@honeybadger-io/js'

// Configure Honeybadger
const configureHoneybadger = () => {
  // Only configure if API key is available and in production
  if (window.honeybadgerApiKey && window.railsEnvironment === 'production') {
    Honeybadger.configure({
      apiKey: window.honeybadgerApiKey,
      environment: 'production',
      revision: window.appVersion,
      
      // Only report uncaught errors in production
      enableUncaught: true,
      // Disable unhandled promise rejection reporting to reduce noise
      enableUnhandledRejection: false,
      
      // Add essential context to errors
      beforeNotify: (notice) => {
        // Skip reporting for known UI interactions that might error
        if (notice.message && (
          notice.message.includes('ResizeObserver loop') ||
          notice.message.includes('Script error') ||
          notice.message.includes('Connection aborted')
        )) {
          return false;
        }

        // 4xx responses are expected business-rule outcomes, not crashes.
        // 401: unauthenticated, 422: validation / date-lock conflicts.
        // 403/404/409 on this endpoint would be real bugs — leave them through.
        const httpStatus = notice.context?.httpStatus;
        if (httpStatus === 401 || httpStatus === 422) {
          return false;
        }
        
        // Add minimal context
        notice.context = notice.context || {};
        notice.context.path = window.location.pathname;
        
        // Add user context if available
        const userIdMeta = document.querySelector('meta[name="current-user-id"]');
        if (userIdMeta) {
          notice.context.userId = userIdMeta.content;
        }
        
        // Add payment flow context
        if (notice.context.component === 'RentalBookingController') {
          const isAdminFlow = window.location.pathname.includes('/admin/');
          notice.context.paymentFlow = isAdminFlow ? 'admin' : 'customer';
          
          // Add critical booking information
          if (notice.context.formData) {
            const { start_date, end_date, payment_status, reservation_status } = notice.context.formData;
            if (start_date && end_date) {
              notice.context.bookingDates = `${start_date} to ${end_date}`;
            }
            if (payment_status) {
              notice.context.paymentStatus = payment_status;
            }
            if (reservation_status) {
              notice.context.reservationStatus = reservation_status;
            }
          }
        }
        
        return notice;
      }
    });
    
    // Monitor payment-related API errors
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      try {
        const response = await originalFetch.apply(this, args);
        
        // Only report server errors (5xx) for critical endpoints
        if (response.status >= 500) {
          const url = typeof args[0] === 'string' ? args[0] : args[0].url;
          const path = new URL(url, window.location.origin).pathname;
          
          // Critical payment and reservation paths
          const criticalPaths = [
            '/api/reservations',
            '/api/payments',
            '/customer/reservations',
            '/admin/reservations',
            '/stripe/webhook'
          ];
          
          if (criticalPaths.some(criticalPath => path.includes(criticalPath))) {
            Honeybadger.notify({
              name: 'PaymentServerError',
              message: `HTTP ${response.status} error on ${path}`,
              context: {
                status: response.status,
                path: path,
                paymentFlow: path.includes('/admin/') ? 'admin' : 'customer'
              },
              fingerprint: `server-error-${response.status}-${path}`
            });
          }
        }
        
        return response;
      } catch (error) {
        // Only report network errors for critical paths
        const url = typeof args[0] === 'string' ? args[0] : args[0].url;
        const path = new URL(url, window.location.origin).pathname;
        
        const criticalPaths = [
          '/api/reservations',
          '/api/payments',
          '/customer/reservations',
          '/admin/reservations',
          '/stripe/webhook'
        ];
        
        if (criticalPaths.some(criticalPath => path.includes(criticalPath))) {
          Honeybadger.notify(error, {
            context: {
              path: path,
              paymentFlow: path.includes('/admin/') ? 'admin' : 'customer'
            },
            fingerprint: `network-error-${path}`
          });
        }
        throw error;
      }
    };
  }
};

// Initialize Honeybadger when the DOM is loaded
document.addEventListener('DOMContentLoaded', configureHoneybadger);

// Also initialize when Turbo navigates
document.addEventListener('turbo:load', configureHoneybadger);

export default Honeybadger;
